const lessonContent = {
  'medical-language-basics': {
    overview: 'Medical language uses standardized word parts to describe anatomy, conditions, procedures, and symptoms precisely. Learning prefixes, roots, suffixes, and abbreviations helps coders interpret clinical documentation accurately.',
    contentSections: [
      {
        heading: 'How medical terms are built',
        body: 'Most medical terms combine a root word with a prefix, suffix, or combining vowel. The root identifies the body part or concept, the prefix modifies meaning, and the suffix often describes a condition, procedure, or specialty.'
      },
      {
        heading: 'Common word parts',
        body: 'Useful prefixes include hyper- for excessive, hypo- for deficient, tachy- for fast, and peri- for around. Common roots include cardi for heart, gastr for stomach, neur for nerve, and oste for bone. Suffixes such as -itis, -ectomy, -algia, and -pathy indicate inflammation, surgical removal, pain, and disease.'
      },
      {
        heading: 'Combining forms and vowels',
        body: 'A combining vowel (usually "o") connects a root to a suffix or to another root to aid pronunciation. For example, in gastroenterology, the combining vowel "o" joins the roots gastr (stomach) and enter (intestines).'
      },
      {
        heading: 'Abbreviations and documentation',
        body: 'Coders frequently encounter approved clinical abbreviations in records. Unclear abbreviations should be verified through approved references or provider clarification rather than interpreted through guesswork.'
      }
    ],
    flashcards: [
      { front: 'What does hyper- mean?', back: 'Excessive, above normal, or greater than usual.' },
      { front: 'What does hypo- mean?', back: 'Deficient, below normal, or under.' },
      { front: 'What does -itis indicate?', back: 'Inflammation.' },
      { front: 'What does -ectomy mean?', back: 'Surgical removal or excision.' },
      { front: 'What does cardi refer to?', back: 'The heart.' },
      { front: 'What does gastr refer to?', back: 'The stomach.' },
      { front: 'What is a combining vowel?', back: 'A vowel (usually "o") used to connect word parts for easier pronunciation.' },
      { front: 'What should you do with an unclear abbreviation?', back: 'Verify its meaning through approved references or provider clarification.' }
    ],
    quiz: [
      {
        id: 'medical-language-basics-q1',
        question: 'Which suffix most commonly indicates inflammation?',
        options: ['-ectomy', '-itis', '-algia', '-scopy'],
        answer: 1,
        explanation: 'The suffix -itis means inflammation, as in arthritis or gastritis.'
      },
      {
        id: 'medical-language-basics-q2',
        question: 'What does the root neur generally refer to?',
        options: ['Nerve', 'Kidney', 'Skin', 'Lung'],
        answer: 0,
        explanation: 'Neur is a combining form associated with nerves or the nervous system.'
      },
      {
        id: 'medical-language-basics-q3',
        question: 'What is the purpose of a combining vowel in medical terms?',
        options: ['To change the word to plural', 'To connect word parts and ease pronunciation', 'To indicate surgical removal', 'To denote past tense'],
        answer: 1,
        explanation: 'Combining vowels (usually "o") join roots and suffixes to make terms easier to pronounce.'
      },
      {
        id: 'medical-language-basics-q4',
        question: 'Which prefix means fast or rapid?',
        options: ['Brady-', 'Tachy-', 'Hypo-', 'Peri-'],
        answer: 1,
        explanation: 'Tachy- means fast or rapid, such as in tachycardia (fast heart rate).'
      }
    ]
  },
  'anatomy-body-systems': {
    overview: 'A practical understanding of anatomy helps coders connect provider documentation to the correct body site and condition. This lesson reviews major body systems, directional language, and anatomical planes used in medical records.',
    contentSections: [
      {
        heading: 'Major body systems',
        body: 'The cardiovascular system circulates blood, the respiratory system supports gas exchange, the digestive system processes nutrients, and the musculoskeletal system provides structure and movement. Other important systems include the nervous, endocrine, urinary, reproductive, integumentary, and immune systems.'
      },
      {
        heading: 'Anatomical specificity',
        body: 'Coding often depends on precise location, laterality, and depth. Documentation may distinguish right from left, proximal from distal, or superficial from deep. Capture these details when the code set provides a corresponding level of specificity.'
      },
      {
        heading: 'Directional terminology',
        body: 'Anterior (ventral) means toward the front and posterior (dorsal) means toward the back. Medial means toward the midline, lateral means away from the midline, proximal means closer to the point of attachment, and distal means farther away.'
      },
      {
        heading: 'Anatomical planes and positions',
        body: 'The sagittal plane divides the body into left and right sections. The frontal (coronal) plane divides the body into anterior and posterior sections, while the transverse (axial) plane divides it into superior (upper) and inferior (lower) halves.'
      }
    ],
    flashcards: [
      { front: 'What does laterality describe?', back: 'The side of the body involved, such as right, left, or bilateral.' },
      { front: 'What does proximal mean?', back: 'Closer to the point of attachment or origin.' },
      { front: 'What does distal mean?', back: 'Farther away from the point of attachment or origin.' },
      { front: 'Which system is responsible for gas exchange?', back: 'The respiratory system.' },
      { front: 'What does medial mean?', back: 'Toward the body’s midline.' },
      { front: 'What does lateral mean?', back: 'Away from the body’s midline.' },
      { front: 'What does the sagittal plane divide?', back: 'Divides the body vertically into right and left sides.' },
      { front: 'What does superior vs. inferior indicate?', back: 'Superior means higher or above; inferior means lower or below.' }
    ],
    quiz: [
      {
        id: 'anatomy-body-systems-q1',
        question: 'Which term means farther from the point of attachment?',
        options: ['Proximal', 'Medial', 'Distal', 'Anterior'],
        answer: 2,
        explanation: 'Distal describes a location farther from the point of attachment or origin.'
      },
      {
        id: 'anatomy-body-systems-q2',
        question: 'Why is laterality important in coding?',
        options: ['It identifies the payer', 'It distinguishes the affected side', 'It determines patient age', 'It replaces the diagnosis'],
        answer: 1,
        explanation: 'Laterality identifies whether the right, left, or both sides are involved when documented.'
      },
      {
        id: 'anatomy-body-systems-q3',
        question: 'Which anatomical plane divides the body into front (anterior) and back (posterior) portions?',
        options: ['Transverse plane', 'Frontal (coronal) plane', 'Sagittal plane', 'Horizontal plane'],
        answer: 1,
        explanation: 'The frontal or coronal plane divides the body into anterior (front) and posterior (back) sections.'
      },
      {
        id: 'anatomy-body-systems-q4',
        question: 'What is the directional term for a structure closer to the body midline?',
        options: ['Lateral', 'Superficial', 'Medial', 'Distal'],
        answer: 2,
        explanation: 'Medial indicates a position toward the imaginary vertical midline of the body.'
      }
    ]
  },
  'health-insurance-fundamentals': {
    overview: 'Health insurance fundamentals explain how healthcare services are financed and how responsibility is divided among payers and patients. Understanding these concepts helps billing professionals interpret benefits, claim outcomes, and patient balances.',
    contentSections: [
      {
        heading: 'Key insurance terms',
        body: 'A premium is the recurring amount paid for coverage. A deductible is the amount a patient may pay before the plan begins sharing costs. Copayments are fixed amounts for specific services, while coinsurance is a percentage of the allowed amount.'
      },
      {
        heading: 'Payers and plans',
        body: 'Commercial insurers, Medicare, Medicaid, workers’ compensation programs, and self-funded employer plans may each have different rules. A payer determines coverage and adjudicates claims according to the member’s benefits and applicable regulations.'
      },
      {
        heading: 'Coordination of benefits',
        body: 'When a patient has more than one insurance plan, coordination of benefits determines which payer is primary and which is secondary. The primary payer processes the claim first, and remaining eligible amounts may then be submitted to the secondary payer.'
      },
      {
        heading: 'Out-of-pocket limits and pre-authorization',
        body: 'The out-of-pocket maximum is the most a patient pays per policy year for covered services. Once reached, the plan pays 100% of allowed charges. Prior authorization requires provider approval before certain non-emergency procedures or specialized care are covered.'
      }
    ],
    flashcards: [
      { front: 'What is a deductible?', back: 'The amount a patient may pay before insurance begins sharing covered costs.' },
      { front: 'What is coinsurance?', back: 'A percentage of the allowed amount that the patient is responsible for.' },
      { front: 'What is a copayment?', back: 'A set dollar amount paid by the patient for a specific covered service.' },
      { front: 'What does the primary payer do?', back: 'Processes the claim first when multiple insurance plans exist.' },
      { front: 'What is a premium?', back: 'A recurring payment made to maintain insurance coverage.' },
      { front: 'What is an out-of-pocket maximum?', back: 'The cap on patient cost-sharing per year; once reached, insurance covers 100% of allowed services.' },
      { front: 'What is prior authorization?', back: 'Formal approval required from an insurer prior to rendering certain non-emergency services.' },
      { front: 'What is an allowed amount?', back: 'The maximum fee an insurance provider agrees to pay for a covered service.' }
    ],
    quiz: [
      {
        id: 'health-insurance-fundamentals-q1',
        question: 'Which term describes a fixed amount paid for a covered service?',
        options: ['Premium', 'Copayment', 'Deductible', 'Allowed amount'],
        answer: 1,
        explanation: 'A copayment is a fixed dollar amount commonly assigned to a specific covered service.'
      },
      {
        id: 'health-insurance-fundamentals-q2',
        question: 'What is the usual order for coordination of benefits?',
        options: ['Secondary then primary', 'Patient then provider', 'Primary then secondary', 'Provider then employer'],
        answer: 2,
        explanation: 'The primary plan processes the claim first, followed by the secondary plan when appropriate.'
      },
      {
        id: 'health-insurance-fundamentals-q3',
        question: 'What happens when a patient reaches their annual out-of-pocket maximum?',
        options: ['They must pay higher premiums', 'The insurance plan pays 100% of allowed charges for covered services', 'Coverage is terminated until next year', 'The patient pays double coinsurance'],
        answer: 1,
        explanation: 'Once the out-of-pocket maximum is met, the plan absorbs 100% of covered healthcare costs for the rest of the benefit year.'
      },
      {
        id: 'health-insurance-fundamentals-q4',
        question: 'What is required under prior authorization rules?',
        options: ['Advance payment by the patient', 'Approval from the insurer before performing specific non-emergency services', 'A secondary insurance policy', 'Immediate claim submission within 24 hours'],
        answer: 1,
        explanation: 'Prior authorization requires obtaining insurance clearance prior to rendering specific services to ensure coverage.'
      }
    ]
  },
  'icd-10-structure': {
    overview: 'Learn how 3-to-7 character diagnosis codes are built, formatted, and specified in clinical practice. ICD-10-CM uses letters, numbers, decimals, placeholders, and extensions to communicate increasingly precise diagnostic information.',
    contentSections: [
      {
        heading: 'Code structure',
        body: 'The first character of an ICD-10-CM code is alphabetic. Characters 2 through 7 may be alphabetic or numeric. The decimal point always follows the third character, as shown in E11.621. Codes may contain between three and seven characters depending on the required specificity.'
      },
      {
        heading: 'Category and subcategory hierarchy',
        body: 'The first three characters designate the category (e.g., E11 for Type 2 diabetes mellitus). Characters 4 through 6 provide additional detail regarding etiology, anatomical site, severity, or laterality. A code must be reported to the highest level of specificity available.'
      },
      {
        heading: 'Placeholder X',
        body: 'The placeholder X fills empty positions when a seventh-character extension is required. It holds the unused position so the seventh character appears in the correct location. This is common in injury code families.'
      },
      {
        heading: 'Seventh characters',
        body: 'Some code families require a seventh character to describe the encounter or healing status. A indicates an initial encounter while the patient receives active treatment, D indicates a subsequent encounter, and S indicates a sequela.'
      }
    ],
    flashcards: [
      { front: 'Decimal Position', back: 'The decimal point always follows the 3rd character.' },
      { front: 'Placeholder X', back: 'Holds empty positions to force a 7th character into the 7th spot.' },
      { front: '7th Char A', back: 'Initial encounter while the patient is receiving active treatment.' },
      { front: '7th Char D', back: 'Subsequent encounter during routine care or healing phase.' },
      { front: '7th Char S', back: 'Sequela; residual effect or condition produced after the acute phase has ended.' },
      { front: 'Category Code', back: 'The first 3 characters of an ICD-10-CM code that represent a broad disease group.' },
      { front: 'Code Length', back: 'ICD-10-CM codes range from 3 to 7 characters in total length.' },
      { front: 'First Character Rule', back: 'The first character of an ICD-10-CM code is always alphabetic.' }
    ],
    quiz: [
      {
        id: 'icd-10-structure-q1',
        question: 'Which character position does the decimal point follow in ICD-10-CM?',
        options: ['2nd', '3rd', '4th', '5th'],
        answer: 1,
        explanation: 'The decimal point always follows the 3rd character.'
      },
      {
        id: 'icd-10-structure-q2',
        question: 'Why is placeholder X used?',
        options: ['Urgent care', 'Experimental code', 'Expands code to the required 7th-character position', 'Unspecified laterality'],
        answer: 2,
        explanation: 'Placeholder X fills empty spaces so a required seventh character appears in the correct position.'
      },
      {
        id: 'icd-10-structure-q3',
        question: 'What does a 7th character of "S" represent in ICD-10-CM coding?',
        options: ['Surgical encounter', 'Sequela (late effect)', 'Subsequent encounter', 'Severe condition'],
        answer: 1,
        explanation: 'The 7th character S indicates a sequela, which is a late effect or residual condition after the acute phase of an injury or illness.'
      },
      {
        id: 'icd-10-structure-q4',
        question: 'What do the first three characters of an ICD-10-CM code represent?',
        options: ['The provider type', 'The category of disease or condition', 'The patient age group', 'The encounter type'],
        answer: 1,
        explanation: 'The initial three characters define the general category of the condition or disease.'
      }
    ]
  },
  'coding-conventions': {
    overview: 'Master critical instructional notes including Excludes1, Excludes2, Code First, and Use Additional Code. These conventions guide code selection, sequencing, and whether related conditions may be reported together.',
    contentSections: [
      {
        heading: 'Excludes1',
        body: 'An Excludes1 note is a pure exclusion. The condition listed is NOT CODED HERE and should not be reported together with the main code because the two conditions cannot occur together under that coding instruction.'
      },
      {
        heading: 'Excludes2',
        body: 'An Excludes2 note means the condition is not included in the current code. The patient may have both conditions at the same time, so both codes may be reported when both conditions are documented and supported.'
      },
      {
        heading: 'Code First and Use Additional Code',
        body: 'Code First directs the coder to report an underlying condition before the manifestation. Use Additional Code indicates that another code is needed to fully describe the condition. Together, these notes help establish the correct sequencing order.'
      },
      {
        heading: 'Punctuation and Inclusion Notes',
        body: 'Brackets [ ] enclose synonyms or explanatory phrases, while parentheses ( ) enclose nonessential modifiers that do not affect code assignment. Inclusion notes appear under code titles to define or give examples of conditions assigned to that category.'
      }
    ],
    flashcards: [
      { front: 'Excludes1', back: 'NOT CODED HERE; the conditions cannot be coded together.' },
      { front: 'Excludes2', back: 'NOT INCLUDED HERE; both conditions can be coded together if present.' },
      { front: 'Code First', back: 'Report the underlying cause or etiology before the manifestation.' },
      { front: 'Use Additional Code', back: 'Report another code when it is needed to fully describe the condition.' },
      { front: 'Nonessential Modifiers', back: 'Words in parentheses ( ) that supplement the description without altering the code assigned.' },
      { front: 'Brackets [ ]', back: 'Used in the Tabular List to enclose synonyms, alternative wording, or explanatory phrases.' },
      { front: 'Inclusion Notes', back: 'Lists of terms or conditions given under a category heading that are included in that code.' },
      { front: 'Code Also', back: 'Instructs the coder that two codes may be required to fully describe a condition, but sequencing depends on the reason for care.' }
    ],
    quiz: [
      {
        id: 'coding-conventions-q1',
        question: 'What does an Excludes1 note mean?',
        options: ['Code both', 'Optional code', 'Not coded here; the conditions cannot be billed together', 'Use modifier -25'],
        answer: 2,
        explanation: 'Excludes1 indicates a pure exclusion and means the listed conditions should not be coded together.'
      },
      {
        id: 'coding-conventions-q2',
        question: 'What does an Excludes2 note indicate?',
        options: ['Both conditions may be reported when supported', 'The patient cannot have either condition', 'Use only an unspecified code', 'The code is deleted'],
        answer: 0,
        explanation: 'Excludes2 means the condition is not included in the current code, but both conditions may exist and be coded together.'
      },
      {
        id: 'coding-conventions-q3',
        question: 'How should a condition with a "Code First" note be sequenced?',
        options: ['After the manifestation code', 'Before the manifestation code', 'Alphabetically', 'In order of billing value'],
        answer: 1,
        explanation: 'Code First mandates that the underlying cause or etiology must be sequenced ahead of the manifestation code.'
      },
      {
        id: 'coding-conventions-q4',
        question: 'What is the purpose of nonessential modifiers in parenthetical statements?',
        options: ['They change the assigned ICD-10 code', 'They provide additional wording that does not affect code assignment', 'They indicate required 7th characters', 'They trigger an automatic audit'],
        answer: 1,
        explanation: 'Parenthetical terms are nonessential modifiers; their presence or absence in documentation does not change the assigned code.'
      }
    ]
  },
  'diagnosis-sequencing': {
    overview: 'Establish rules for determining the principal versus secondary diagnosis across inpatient and outpatient care. Correct sequencing reflects the reason for the encounter and the conditions that affected treatment or care.',
    contentSections: [
      {
        heading: 'Outpatient first-listed diagnosis',
        body: 'The first-listed diagnosis is the primary condition responsible for the services rendered and the chief reason for the outpatient encounter after evaluation. It must be supported by provider documentation.'
      },
      {
        heading: 'Inpatient principal diagnosis',
        body: 'For inpatient care, the principal diagnosis is the condition established after study to be chiefly responsible for the admission. Inpatient sequencing rules differ from outpatient first-listed diagnosis rules.'
      },
      {
        heading: 'Secondary diagnoses',
        body: 'Secondary diagnoses are co-existing conditions that require evaluation, treatment, monitoring, or affect patient care during the encounter. Do not report conditions solely because they appear in the record if they do not meet reporting requirements.'
      },
      {
        heading: 'Unconfirmed diagnoses and symptoms',
        body: 'In outpatient settings, do not report codes described as "probable," "suspected," or "rule out." Instead, code the documented symptoms or signs. Inpatient guidelines allow coding unconfirmed diagnoses at discharge if documented as probable.'
      }
    ],
    flashcards: [
      { front: 'First-Listed Diagnosis', back: 'The primary reason for an outpatient encounter and the condition responsible for services rendered.' },
      { front: 'Principal Diagnosis', back: 'The condition established after study as chiefly responsible for an inpatient admission.' },
      { front: 'Secondary Diagnosis', back: 'A co-existing condition that affects evaluation, treatment, monitoring, or care.' },
      { front: 'Outpatient Unconfirmed Diagnoses', back: 'Do not code suspected or rule-out conditions; code symptoms or signs instead.' },
      { front: 'Inpatient Unconfirmed Diagnoses', back: 'Code unconfirmed/probable diagnoses at discharge as if established, unless guidelines state otherwise.' },
      { front: 'Comorbidity', back: 'A pre-existing condition that coexists alongside the principal diagnosis during care.' },
      { front: 'Complication', back: 'A condition arising during an inpatient hospital stay that prolongs or modifies care.' },
      { front: 'Acute vs. Chronic Sequencing', back: 'If a condition is documented as both acute and chronic, code both and sequence the acute code first.' }
    ],
    quiz: [
      {
        id: 'diagnosis-sequencing-q1',
        question: 'In outpatient billing, how is the first-listed diagnosis determined?',
        options: ['Highest-paying code', 'Primary condition responsible for services rendered', 'Random selection', 'First condition alphabetically'],
        answer: 1,
        explanation: 'The first-listed diagnosis is the primary condition established during the encounter and responsible for the services.'
      },
      {
        id: 'diagnosis-sequencing-q2',
        question: 'When may a secondary diagnosis be reported?',
        options: ['Whenever it appears in the chart', 'When it affects evaluation, treatment, monitoring, or care', 'Only when it is the highest-paying diagnosis', 'Never in outpatient care'],
        answer: 1,
        explanation: 'Secondary diagnoses must meet reporting requirements and affect the patient’s care or services.'
      },
      {
        id: 'diagnosis-sequencing-q3',
        question: 'How are "probable" or "rule out" diagnoses reported in the outpatient setting?',
        options: ['Code them as established diagnoses', 'Do not code them; report the presenting symptoms instead', 'Use modifier -22', 'Report them as primary diagnoses'],
        answer: 1,
        explanation: 'Outpatient guidelines prohibit coding unconfirmed diagnoses; coders must report the signs or symptoms presented.'
      },
      {
        id: 'diagnosis-sequencing-q4',
        question: 'If a condition is documented as both acute and chronic with separate codes in the Tabular List, how are they sequenced?',
        options: ['Chronic code first', 'Acute code first', 'Only code the acute condition', 'Only code the chronic condition'],
        answer: 1,
        explanation: 'When separate codes exist for acute and chronic forms of the same condition, sequence the acute code first.'
      }
    ]
  },
  'cpt-codebook': {
    overview: 'Understand Category I, II, and III code structures and standard CPT section organization. CPT provides a standardized language for reporting physician and outpatient procedures and services.',
    contentSections: [
      {
        heading: 'Category I codes',
        body: 'Category I codes contain five numeric digits and represent standard procedures and services across six primary sections: Evaluation and Management, Anesthesia, Surgery, Radiology, Pathology and Laboratory, and Medicine.'
      },
      {
        heading: 'Category II codes',
        body: 'Category II codes contain four numbers followed by the letter F. They are supplemental tracking codes used to report performance measurement and quality-related information.'
      },
      {
        heading: 'Category III codes',
        body: 'Category III codes contain four numbers followed by the letter T. They identify emerging technologies, services, and procedures that may not yet have permanent Category I code assignments.'
      },
      {
        heading: 'CPT Code Conventions and Symbols',
        body: 'CPT uses distinct symbols to convey specific instructions. A bullet symbol (•) indicates a new code, a triangle (▲) denotes a revised code description, and lightning bolts indicate FDA approval pending. Indented codes rely on the main code description up to the semicolon.'
      }
    ],
    flashcards: [
      { front: 'Category I', back: 'Standard 5-digit numeric procedural codes.' },
      { front: 'Category II', back: 'Four numbers followed by F for performance tracking.' },
      { front: 'Category III', back: 'Four numbers ending in T for emerging technologies.' },
      { front: 'Bullet Symbol (•)', back: 'Indicates a new code added to the CPT manual for the current edition.' },
      { front: 'Triangle Symbol (▲)', back: 'Indicates that the text of an existing code description has been revised.' },
      { front: 'Semicolon (;) Usage', back: 'Separates common main terms from indented, specific sub-entries in CPT.' },
      { front: 'Plus Symbol (+)', back: 'Identifies an add-on code that must be reported in conjunction with a primary code.' },
      { front: 'Forbidden Symbol (🚫)', back: 'Identifies codes that are exempt from the use of modifier -51.' }
    ],
    quiz: [
      {
        id: 'cpt-codebook-q1',
        question: 'How are CPT Category III codes formatted?',
        options: ['5 digits', '4 numbers plus F', '4 numbers plus T', '1 letter plus 4 numbers'],
        answer: 2,
        explanation: 'Category III codes contain four numbers followed by T and identify emerging technologies.'
      },
      {
        id: 'cpt-codebook-q2',
        question: 'Which CPT section includes physician office visits?',
        options: ['Evaluation and Management', 'Anesthesia', 'Radiology', 'Medicine supplies'],
        answer: 0,
        explanation: 'Evaluation and Management codes include many physician office and outpatient visit services.'
      },
      {
        id: 'cpt-codebook-q3',
        question: 'What does a bullet symbol (•) next to a CPT code signify?',
        options: ['Revised description', 'New code addition', 'Add-on code', 'Pending FDA approval'],
        answer: 1,
        explanation: 'A bullet symbol indicates a new code added to the CPT code set for that edition year.'
      },
      {
        id: 'cpt-codebook-q4',
        question: 'What is the primary function of CPT Category II codes?',
        options: ['Emerging medical technology', 'Durable medical equipment billing', 'Performance measurement and quality tracking', 'Inpatient operative procedure coding'],
        answer: 2,
        explanation: 'Category II codes (ending in F) are optional tracking codes used for quality measures like HEDIS.'
      }
    ]
  },
  'modifiers-basics': {
    overview: 'Master two-character modifiers that add vital context to procedural codes without altering their base definitions. Modifiers explain distinct services, increased effort, and special circumstances when documentation supports their use.',
    contentSections: [
      {
        heading: 'Modifier 25',
        body: 'Modifier 25 identifies a significant, separately identifiable Evaluation and Management service performed by the same provider on the same day as another procedure or service.'
      },
      {
        heading: 'Modifier 59',
        body: 'Modifier 59 identifies a distinct procedural service. It may be used to communicate that services were separate when clinically appropriate and supported by documentation, but it should not be used only to bypass an edit.'
      },
      {
        heading: 'Modifier 22',
        body: 'Modifier 22 indicates increased procedural services when the work required substantially greater effort than normally required. Documentation should explain the additional work and circumstances.'
      },
      {
        heading: 'Anatomic and Technical Modifiers',
        body: 'Anatomic modifiers (such as RT, LT, E1-E4, FA-F9) pinpoint exact physical sites. Technical modifiers like modifier 26 (Professional Component) and TC (Technical Component) split service reimbursement between physician work and facility equipment utilization.'
      }
    ],
    flashcards: [
      { front: 'Modifier 25', back: 'A significant, separately identifiable E/M service on the same day as a procedure.' },
      { front: 'Modifier 59', back: 'A distinct procedural service.' },
      { front: 'Modifier 22', back: 'Increased procedural services requiring substantially greater effort.' },
      { front: 'Modifier 26', back: 'Professional Component; covers physician interpretation and supervision.' },
      { front: 'Modifier TC', back: 'Technical Component; covers equipment, facility overhead, and staff.' },
      { front: 'Modifier 50', back: 'Bilateral Procedure performed on both sides of the body during the same session.' },
      { front: 'Modifier 51', back: 'Multiple Procedures performed during the same session by the same provider.' },
      { front: 'X{EPSU} Modifiers', back: 'CMS subset modifiers (XE, XP, XS, XU) providing specific detail to replace generic modifier 59.' }
    ],
    quiz: [
      {
        id: 'modifiers-basics-q1',
        question: 'Which modifier is added to an E/M service performed on the same day as a minor procedure?',
        options: ['-50', '-25', '-59', '-22'],
        answer: 1,
        explanation: 'Modifier -25 indicates a significant, separately identifiable E/M service performed on the same day as another procedure.'
      },
      {
        id: 'modifiers-basics-q2',
        question: 'What does modifier -59 communicate?',
        options: ['Bilateral service', 'Distinct procedural service', 'Reduced service', 'Unrelated diagnosis'],
        answer: 1,
        explanation: 'Modifier -59 identifies a distinct procedural service when the documentation supports its use.'
      },
      {
        id: 'modifiers-basics-q3',
        question: 'Which modifier splits a diagnostic test to report only the physician interpretation?',
        options: ['Modifier TC', 'Modifier 26', 'Modifier 52', 'Modifier 53'],
        answer: 1,
        explanation: 'Modifier 26 isolates the professional component, such as a radiologist reading an X-ray.'
      },
      {
        id: 'modifiers-basics-q4',
        question: 'What does Modifier 50 indicate when appended to a surgical code?',
        options: ['Procedure was discontinued', 'Procedure was performed bilaterally', 'Procedure was performed by two surgeons', 'Procedure was staged'],
        answer: 1,
        explanation: 'Modifier 50 is reported when a procedure is performed bilaterally (on both left and right sides).'
      }
    ]
  },
  'hcpcs-level-two': {
    overview: 'Learn alphanumeric coding for durable medical equipment, supplies, drugs, and non-physician services. HCPCS Level II expands standardized reporting beyond the physician procedures represented by CPT Level I.',
    contentSections: [
      {
        heading: 'HCPCS Level II format',
        body: 'HCPCS Level II codes generally contain a single alphabetic character followed by four numeric digits. For example, E0601 represents a CPAP device. The code must be supported by documentation, medical necessity, and applicable payer rules.'
      },
      {
        heading: 'Level I versus Level II',
        body: 'CPT is HCPCS Level I and primarily reports physician and outpatient procedures and services. HCPCS Level II reports products and services such as supplies, durable medical equipment, drugs, ambulance transportation, and orthotics.'
      },
      {
        heading: 'Units and documentation',
        body: 'Many Level II codes require units representing the quantity supplied, administered, or transported. The medical record should support the product, quantity, dosage when applicable, medical necessity, and place of service.'
      },
      {
        heading: 'HCPCS National Modifiers',
        body: 'HCPCS Level II modifiers are two-letter or alphanumeric modifiers (e.g., GA, NU, RR, KX) that indicate rental versus purchase status, specific clinical policy compliance, or waiver of liability forms on file.'
      }
    ],
    flashcards: [
      { front: 'HCPCS Level II Format', back: 'One alphabetic character followed by four numbers.' },
      { front: 'HCPCS Level II', back: 'Codes for supplies, DME, drugs, ambulance services, and other non-physician services.' },
      { front: 'HCPCS Level I', back: 'CPT codes used primarily for physician and outpatient procedures and services.' },
      { front: 'Modifier RR', back: 'HCPCS Level II modifier indicating Rental of Durable Medical Equipment.' },
      { front: 'Modifier NU', back: 'HCPCS Level II modifier indicating Purchase of New Equipment.' },
      { front: 'J-Codes', back: 'HCPCS Level II code category for injectable non-oral medications and chemotherapy drugs.' },
      { front: 'E-Codes', back: 'HCPCS Level II code category for Durable Medical Equipment (DME).' },
      { front: 'Modifier KX', back: 'Indicates specific medical necessity requirements met per coverage policy.' }
    ],
    quiz: [
      {
        id: 'hcpcs-level-two-q1',
        question: 'What does HCPCS Level II primarily cover?',
        options: ['Inpatient surgeries', 'Supplies, DME, drugs, and ambulance services', 'Office visits', 'Anesthesia'],
        answer: 1,
        explanation: 'HCPCS Level II covers supplies, equipment, medications, ambulance services, and other non-physician services.'
      },
      {
        id: 'hcpcs-level-two-q2',
        question: 'What is the general format of a HCPCS Level II code?',
        options: ['Five numeric digits', 'One letter followed by four numbers', 'Two letters and two numbers', 'A decimal diagnosis code'],
        answer: 1,
        explanation: 'HCPCS Level II codes generally use one alphabetic character followed by four numeric digits.'
      },
      {
        id: 'hcpcs-level-two-q3',
        question: 'Which HCPCS Level II code section covers injectable medications (non-oral)?',
        options: ['A-codes', 'E-codes', 'J-codes', 'L-codes'],
        answer: 2,
        explanation: 'J-codes are used to report administered drugs and medications that cannot be self-administered.'
      },
      {
        id: 'hcpcs-level-two-q4',
        question: 'What does modifier RR indicate when attached to a HCPCS Level II DME code?',
        options: ['Replacement item', 'Rental equipment', 'Repair needed', 'Recalled device'],
        answer: 1,
        explanation: 'Modifier RR signifies that the durable medical equipment was provided on a rental basis.'
      }
    ]
  },
  'revenue-cycle-overview': {
    overview: 'Follow patient claims from registration to charge capture, adjudication, and final resolution. The healthcare revenue cycle connects patient access, clinical documentation, coding, billing, payment posting, and follow-up.',
    contentSections: [
      {
        heading: 'The five major stages',
        body: 'The revenue cycle commonly follows five stages: 1. Intake and eligibility, 2. Charge capture and coding, 3. Claim scrubbing, 4. Payer adjudication, and 5. Payment and EOB or ERA review.'
      },
      {
        heading: 'Front-end operations',
        body: 'Patient intake, registration, insurance verification, authorization, and eligibility checks occur before or around the time of service. Accurate information at this stage prevents avoidable billing delays.'
      },
      {
        heading: 'Back-end operations',
        body: 'Back-end work includes claim submission, payment posting, denial management, patient statements, collections, account resolution, and analyzing performance metrics such as clean claim rate and days in accounts receivable.'
      },
      {
        heading: 'Key Performance Indicators (KPIs)',
        body: 'Revenue cycle success is tracked using key metrics. Days in Accounts Receivable (DAR) measures the average time to receive payment, Net Collection Rate evaluates financial yield, and the Clean Claim Rate tracks claims paid on initial submission.'
      }
    ],
    flashcards: [
      { front: 'Claim Scrubbing', back: 'Pre-clearinghouse edit checks that verify syntax, required fields, and valid codes.' },
      { front: 'Adjudication', back: 'The payer’s process of reviewing a claim and determining payment or denial.' },
      { front: 'ERA', back: 'Electronic Remittance Advice explaining payer payment decisions and adjustments.' },
      { front: 'Days in A/R', back: 'The average number of days it takes for a practice to collect payments due.' },
      { front: 'Clean Claim Rate', back: 'The percentage of claims accepted and processed on their first submission.' },
      { front: 'Charge Capture', back: 'The process of recording billable services and supplies rendered to a patient.' },
      { front: 'Prior Authorization', back: 'Payer approval required before receiving specific non-emergency services.' },
      { front: 'Balance Billing', back: 'Billing a patient for the difference between provider charge and allowed amount.' }
    ],
    quiz: [
      {
        id: 'revenue-cycle-overview-q1',
        question: 'What is the first stage of the revenue cycle?',
        options: ['Coding', 'Patient Intake and Eligibility', 'Appeals', 'Scrubbing'],
        answer: 1,
        explanation: 'Intake and eligibility verification start the revenue cycle by establishing patient and coverage information.'
      },
      {
        id: 'revenue-cycle-overview-q2',
        question: 'What happens during claim scrubbing?',
        options: ['The patient receives treatment', 'Claims are checked for errors before submission', 'Payments are collected in cash', 'Records are destroyed'],
        answer: 1,
        explanation: 'Claim scrubbing checks syntax, required information, and coding validity before a claim reaches the payer.'
      },
      {
        id: 'revenue-cycle-overview-q3',
        question: 'Which metric measures the average time required to collect payment on claims?',
        options: ['Clean Claim Rate', 'Days in Accounts Receivable (A/R)', 'Net Collection Ratio', 'Denial Frequency Index'],
        answer: 1,
        explanation: 'Days in Accounts Receivable measures how long, on average, claims sit outstanding before payment.'
      },
      {
        id: 'revenue-cycle-overview-q4',
        question: 'What is the purpose of prior authorization during front-end operations?',
        options: ['To confirm the patient’s home address', 'To obtain payer approval before service delivery', 'To submit the final post-procedure bill', 'To scrub the claim for syntax errors'],
        answer: 1,
        explanation: 'Prior authorization ensures the insurer agrees that the planned procedure meets medical necessity before service.'
      }
    ]
  },
  'clean-claims': {
    overview: 'Differentiate CMS-1500 and UB-04 forms and learn how clearinghouses scrub claims. Clean claims contain complete, accurate, and consistent information that can be processed without avoidable intervention.',
    contentSections: [
      {
        heading: 'CMS-1500 and 837P',
        body: 'The CMS-1500 form and its electronic equivalent, the 837P, are used for professional physician and outpatient claims. These claims commonly report provider services and professional fees.'
      },
      {
        heading: 'UB-04 and 837I',
        body: 'The UB-04, also called CMS-1450, and its electronic equivalent, the 837I, are used for institutional and hospital facility claims. They report facility services and institutional charges.'
      },
      {
        heading: 'ERA and EOB',
        body: 'An ERA or EOB provides remittance information about payment decisions, allowed amounts, adjustments, patient responsibility, and reason codes. These details guide payment posting and follow-up.'
      },
      {
        heading: 'Clearinghouse Role and NPIs',
        body: 'Clearinghouses act as intermediaries between billing systems and payers, formatting claims to HIPAA standards. Claims require valid National Provider Identifiers (NPIs) for rendering, billing, and referring providers.'
      }
    ],
    flashcards: [
      { front: 'CMS-1500', back: 'Professional physician claim form, electronically represented as 837P.' },
      { front: 'UB-04', back: 'Institutional or hospital claim form, electronically represented as 837I.' },
      { front: 'ERA / EOB', back: 'Remittance information detailing payment decisions, adjustments, and responsibility.' },
      { front: 'Clearinghouse', back: 'Entity that formats, scrubs, and routes electronic claims between providers and payers.' },
      { front: 'NPI', back: 'National Provider Identifier; 10-digit unique identification number for healthcare providers.' },
      { front: '837P', back: 'The standard HIPAA electronic transaction set for professional health claims.' },
      { front: '837I', back: 'The standard HIPAA electronic transaction set for institutional health claims.' },
      { front: 'Clearinghouse Edit', back: 'An automated check that stops invalid claims prior to payer delivery.' }
    ],
    quiz: [
      {
        id: 'clean-claims-q1',
        question: 'Which form is used for professional physician billing?',
        options: ['UB-04', 'CMS-1500', 'CMS-1450', 'W-2'],
        answer: 1,
        explanation: 'CMS-1500 is the standard professional claim form, while UB-04/CMS-1450 is used for institutional claims.'
      },
      {
        id: 'clean-claims-q2',
        question: 'What does an ERA or EOB communicate?',
        options: ['Employee scheduling', 'Payer payment decisions and adjustments', 'A patient’s medical history only', 'Provider credentials'],
        answer: 1,
        explanation: 'ERA and EOB documents explain how the payer adjudicated the claim and calculated responsibility.'
      },
      {
        id: 'clean-claims-q3',
        question: 'What is the electronic equivalent of the paper UB-04 claim form?',
        options: ['837P', '837I', '835', '270'],
        answer: 1,
        explanation: 'The 837I transaction is the electronic equivalent of the UB-04 institutional claim form.'
      },
      {
        id: 'clean-claims-q4',
        question: 'How many digits make up a standard National Provider Identifier (NPI)?',
        options: ['9 digits', '10 digits', '12 digits', '7 digits'],
        answer: 1,
        explanation: 'An NPI is a standard 10-digit national identifier assigned to US healthcare providers.'
      }
    ]
  },
  'denial-management': {
    overview: 'Understand the operational difference between claim rejections and claim denials. Prevention and follow-up depend on identifying whether a claim failed before processing or was processed and refused by the payer.',
    contentSections: [
      {
        heading: 'Claim rejection',
        body: 'A rejection is an unprocessed claim returned upfront because of formatting, missing information, invalid codes, or other syntax errors. The claim must usually be corrected and resubmitted.'
      },
      {
        heading: 'Claim denial',
        body: 'A denial is a fully adjudicated claim that was processed by the payer but refused reimbursement. Denials may require correction, reconsideration, or an appeal with supporting documentation.'
      },
      {
        heading: 'Prevention and follow-up',
        body: 'Review rejection and denial reason codes, validate the original claim and documentation, identify the appropriate correction or appeal, submit required materials, and track outcomes. Trend analysis helps prevent recurring problems.'
      },
      {
        heading: 'CARCs and RARCs',
        body: 'Payers use Claim Adjustment Reason Codes (CARCs) and Remittance Advice Remark Codes (RARCs) on the ERA/EOB to explain why a claim was adjusted, reduced, or denied reimbursement.'
      }
    ],
    flashcards: [
      { front: 'Claim Rejection', back: 'An unprocessed claim returned because of a syntax, formatting, or front-end error.' },
      { front: 'Claim Denial', back: 'An adjudicated claim that was processed but refused reimbursement by the payer.' },
      { front: 'Denial Appeal', back: 'A formal request for the payer to reconsider a processed claim decision.' },
      { front: 'CARC', back: 'Claim Adjustment Reason Code explaining financial adjustments on remittance.' },
      { front: 'RARC', back: 'Remittance Advice Remark Code providing additional details on CARCs.' },
      { front: 'Timely Filing Limit', back: 'Payer deadline within which a claim or appeal must be submitted.' },
      { front: 'Write-off', back: 'Uncollectible balance removed from financial accounts receivable.' },
      { front: 'Redetermination', back: 'First level of formal appeal in Medicare denial processing.' }
    ],
    quiz: [
      {
        id: 'denial-management-q1',
        question: 'What distinguishes a rejection from a denial?',
        options: ['No difference', 'Rejections are returned unprocessed; denials are processed and refused', 'Denials pay more', 'Rejections apply only to Medicare'],
        answer: 1,
        explanation: 'Rejections fail before normal adjudication, while denials occur after the payer has processed the claim.'
      },
      {
        id: 'denial-management-q2',
        question: 'What is commonly required for a denied claim?',
        options: ['Ignoring the claim', 'An appeal or corrective follow-up', 'Deleting the patient record', 'Changing the patient’s insurance'],
        answer: 1,
        explanation: 'A denied claim may require correction, reconsideration, or an appeal supported by documentation.'
      },
      {
        id: 'denial-management-q3',
        question: 'What do CARCs and RARCs on an ERA explain?',
        options: ['Patient appointment dates', 'Specific reason codes for claim adjustments and denials', 'Provider credentialing expiration', 'Clearinghouse routing numbers'],
        answer: 1,
        explanation: 'Claim Adjustment Reason Codes and Remittance Advice Remark Codes specify why a claim was denied or adjusted.'
      },
      {
        id: 'denial-management-q4',
        question: 'What is the risk of missing a payer’s timely filing deadline for an appeal?',
        options: ['Automatic full payment', 'Permanent contractual denial with no right to balance bill', 'A 5% penalty fee', 'Temporary claim hold'],
        answer: 1,
        explanation: 'Missing timely filing limits usually results in an unappealable denial that cannot be billed to the patient.'
      }
    ]
  },
  'hipaa-privacy': {
    overview: 'Navigate rules safeguarding Protected Health Information and Electronic Protected Health Information. HIPAA privacy and security practices limit inappropriate access, use, disclosure, and transmission of patient information.',
    contentSections: [
      {
        heading: 'Privacy Rule',
        body: 'The HIPAA Privacy Rule protects identifiable patient information across written, oral, and digital formats. Names, dates, medical record numbers, diagnoses, claim details, and other linked information may constitute PHI.'
      },
      {
        heading: 'Security Rule',
        body: 'The Security Rule establishes technical, administrative, and physical safeguards specifically for electronic PHI. These safeguards help protect information from unauthorized access, alteration, disclosure, or loss.'
      },
      {
        heading: 'Minimum Necessary Rule',
        body: 'The minimum necessary rule requires sharing only the bare minimum PHI needed to complete a task. Staff should access only the records and fields required for their responsibilities and should report suspected incidents through approved channels.'
      },
      {
        heading: 'Breach Notification Rule',
        body: 'The Breach Notification Rule requires covered entities and business associates to notify affected individuals, the Secretary of HHS, and in some cases, the media, following an impermissible use or disclosure of unsecured PHI.'
      }
    ],
    flashcards: [
      { front: 'PHI', back: 'Protected Health Information.' },
      { front: 'ePHI', back: 'Electronic Protected Health Information.' },
      { front: 'Minimum Necessary Rule', back: 'Limit PHI disclosure to the minimum required for the task.' },
      { front: 'Security Rule', back: 'HIPAA requirements for protecting electronic PHI with administrative, physical, and technical safeguards.' },
      { front: 'Privacy Rule', back: 'HIPAA standard protecting identifiable patient information across all formats (written, oral, digital).' },
      { front: 'Breach Notification Rule', back: 'Requires covered entities to report unauthorized access or disclosure of unsecured PHI.' },
      { front: 'Covered Entity', back: 'Healthcare providers, health plans, and healthcare clearinghouses subject to HIPAA.' },
      { front: 'Business Associate', back: 'A third-party vendor that performs functions involving the use or disclosure of PHI.' }
    ],
    quiz: [
      {
        id: 'hipaa-privacy-q1',
        question: 'What does the Minimum Necessary Rule require?',
        options: ['Keep three years of notes', 'Share only the minimum PHI required', 'Charge minimal fees', 'Use one code per claim'],
        answer: 1,
        explanation: 'The rule requires disclosure of only the bare minimum PHI needed for the task.'
      },
      {
        id: 'hipaa-privacy-q2',
        question: 'Which HIPAA rule specifically addresses electronic PHI safeguards?',
        options: ['Privacy Rule', 'Security Rule', 'Billing Rule', 'Coding Rule'],
        answer: 1,
        explanation: 'The Security Rule establishes safeguards for electronic protected health information.'
      },
      {
        id: 'hipaa-privacy-q3',
        question: 'Who must be notified according to the HIPAA Breach Notification Rule if a major breach occurs?',
        options: ['Only the patient', 'Only the hospital CEO', 'Affected individuals, the Secretary of HHS, and potentially media', 'Only the local police'],
        answer: 2,
        explanation: 'Major breaches require notification to patients, the federal government (HHS), and often prominent media outlets.'
      },
      {
        id: 'hipaa-privacy-q4',
        question: 'Which of the following is considered a "Covered Entity" under HIPAA?',
        options: ['A third-party IT vendor', 'A patient’s employer', 'A health insurance plan', 'A medical transcription service'],
        answer: 2,
        explanation: 'Health plans, healthcare clearinghouses, and healthcare providers are defined as covered entities. IT vendors and transcription services are business associates.'
      }
    ]
  },
  'fraud-waste-abuse': {
    overview: 'Distinguish deliberate fraudulent billing from unintentional abuse and compliance errors. Accurate coding, complete documentation, and appropriate reporting help protect patients, payers, and healthcare organizations.',
    contentSections: [
      {
        heading: 'Fraud',
        body: 'Fraud involves knowingly and willfully deceiving or misrepresenting facts for financial gain. Examples include upcoding, billing for phantom services, falsifying records, and submitting claims for services that were not performed.'
      },
      {
        heading: 'Abuse and waste',
        body: 'Abuse involves improper financial or clinical practices that cause excessive costs without proven intent to deceive. Waste generally refers to unnecessary or inefficient use of healthcare resources.'
      },
      {
        heading: 'Upcoding and unbundling',
        body: 'Upcoding means billing a higher service level than documentation supports. Unbundling means reporting components of a bundled service separately in an attempt to obtain higher payment.'
      },
      {
        heading: 'Medical Necessity',
        body: 'Services must be reasonable and necessary for the diagnosis or treatment of illness or injury. Billing for services that lack established medical necessity, even if performed, is a common source of waste and potential abuse.'
      }
    ],
    flashcards: [
      { front: 'Fraud', back: 'Knowingly and willfully misrepresenting facts for financial gain.' },
      { front: 'Upcoding', back: 'Billing a higher code level than the documentation supports.' },
      { front: 'Unbundling', back: 'Billing bundled procedural components separately for higher payment.' },
      { front: 'Abuse', back: 'An improper practice causing excessive costs without proven intent to deceive.' },
      { front: 'Waste', back: 'Overutilization of services or other practices that result in unnecessary costs.' },
      { front: 'Medical Necessity', back: 'Criteria that a service is reasonable and necessary for treatment.' },
      { front: 'Phantom Billing', back: 'Billing for services, supplies, or equipment that were never provided.' },
      { front: 'Cloned Notes', back: 'Copying and pasting medical documentation without updating it for the specific encounter.' }
    ],
    quiz: [
      {
        id: 'fraud-waste-abuse-q1',
        question: 'Intentionally billing a 45-minute visit when only a 10-minute checkup occurred is an example of what?',
        options: ['Upcoding', 'Unbundling', 'Abuse', 'Clean claim'],
        answer: 0,
        explanation: 'Upcoding is billing a higher-level service than the service actually rendered and documented.'
      },
      {
        id: 'fraud-waste-abuse-q2',
        question: 'Which example best describes unbundling?',
        options: ['Correcting a patient address', 'Billing bundled components separately for higher payment', 'Verifying eligibility', 'Posting a payer payment'],
        answer: 1,
        explanation: 'Unbundling separates services that should be reported together under applicable coding rules.'
      },
      {
        id: 'fraud-waste-abuse-q3',
        question: 'What differentiates "fraud" from "abuse"?',
        options: ['Fraud only applies to Medicare', 'Fraud requires proven intent to deceive', 'Abuse results in higher financial penalties', 'Abuse involves physical harm to patients'],
        answer: 1,
        explanation: 'Fraud is an intentional misrepresentation for gain, while abuse lacks the clear intent to deceive but still causes improper payment.'
      },
      {
        id: 'fraud-waste-abuse-q4',
        question: 'Billing for a brand-name drug when a generic drug was actually dispensed is an example of:',
        options: ['Waste', 'Unbundling', 'Phantom Billing', 'Upcoding/Fraud'],
        answer: 3,
        explanation: 'This is a form of upcoding/fraud, as the provider is intentionally claiming a more expensive item than was provided.'
      }
    ]
  },
  'coding-compliance-programs': {
    overview: 'Review federal integrity laws including the False Claims Act, Anti-Kickback Statute, and Stark Law. Compliance programs combine policies, training, audits, reporting channels, and corrective action to support ethical coding and billing.',
    contentSections: [
      {
        heading: 'False Claims Act',
        body: 'The False Claims Act penalizes knowingly submitting false or fraudulent claims to federal healthcare programs. Claims must be supported by accurate documentation and appropriate coding.'
      },
      {
        heading: 'Anti-Kickback Statute',
        body: 'The Anti-Kickback Statute criminalizes knowingly paying, offering, receiving, or soliciting something of value to influence referrals or generate business reimbursed by federal healthcare programs.'
      },
      {
        heading: 'Stark Law',
        body: 'The Stark Law prohibits certain physician self-referrals to entities in which the physician or an immediate family member has a financial relationship, subject to applicable exceptions.'
      },
      {
        heading: 'Compliance program operations',
        body: 'A strong compliance program includes written policies, leadership oversight, education, confidential communication channels, auditing, monitoring, corrective action, and procedures for responding to concerns.'
      }
    ],
    flashcards: [
      { front: 'Stark Law', back: 'Prohibits physician self-referrals to entities in which the physician has a financial interest.' },
      { front: 'False Claims Act', back: 'Penalizes knowingly submitting false claims to federal healthcare programs.' },
      { front: 'Anti-Kickback Statute', back: 'Prohibits rewards or remuneration intended to influence referrals.' },
      { front: 'Compliance Program', back: 'A structured system of policies, education, audits, reporting, and corrective action.' },
      { front: 'OIG', back: 'Office of Inspector General; enforces federal healthcare compliance laws.' },
      { front: 'Qui Tam', back: 'Whistleblower provision of the False Claims Act allowing individuals to sue on behalf of the government.' },
      { front: 'Internal Audit', back: 'Routine, internal reviews of coding and billing accuracy to maintain compliance.' },
      { front: 'Safe Harbors', back: 'Specific exceptions that protect certain business arrangements from Anti-Kickback prosecution.' }
    ],
    quiz: [
      {
        id: 'coding-compliance-programs-q1',
        question: 'Which law targets physician self-referrals to financial partners?',
        options: ['Stark Law', 'HIPAA', 'EMTALA', 'OIG Rule'],
        answer: 0,
        explanation: 'Stark Law restricts certain physician self-referrals involving financial relationships.'
      },
      {
        id: 'coding-compliance-programs-q2',
        question: 'Which law addresses false claims submitted to federal healthcare programs?',
        options: ['False Claims Act', 'Stark Law', 'Security Rule', 'CPT Guidelines'],
        answer: 0,
        explanation: 'The False Claims Act addresses knowingly submitting false or fraudulent federal claims.'
      },
      {
        id: 'coding-compliance-programs-q3',
        question: 'A pharmaceutical company offering an all-expenses-paid vacation to a doctor who prescribes their new drug violates which law?',
        options: ['HIPAA Privacy Rule', 'Stark Law', 'Anti-Kickback Statute', 'Minimum Necessary Rule'],
        answer: 2,
        explanation: 'The Anti-Kickback Statute prohibits offering remuneration (like a vacation) to induce referrals or prescriptions paid by federal programs.'
      },
      {
        id: 'coding-compliance-programs-q4',
        question: 'What is a "Qui Tam" relator?',
        options: ['A federal compliance auditor', 'A whistleblower who files a lawsuit on behalf of the government under the False Claims Act', 'A physician who self-refers', 'A specific type of safe harbor exception'],
        answer: 1,
        explanation: 'The Qui Tam provision of the False Claims Act allows private citizens (whistleblowers) to sue entities defrauding the government and share in the recovered funds.'
      }
    ]
  }
};

const baseDomains = [
  {
    id: 'foundations-medical-terminology',
    number: '01',
    title: 'Foundations & Medical Terminology',
    shortTitle: 'Foundations',
    description: 'Build a strong vocabulary and understand the anatomy, documentation, and insurance concepts behind accurate coding.',
    icon: 'BookOpen',
    accent: 'teal',
    lessons: [
      {
        id: 'medical-language-basics',
        title: 'Medical Language Basics',
        description: 'Decode common prefixes, suffixes, roots, abbreviations, and directional terms used in clinical documentation.',
        readTime: 8,
        completed: false
      },
      {
        id: 'anatomy-body-systems',
        title: 'Anatomy and Body Systems',
        description: 'Review major body systems and the anatomical terminology coders encounter in medical records.',
        readTime: 12,
        completed: false
      },
      {
        id: 'health-insurance-fundamentals',
        title: 'Health Insurance Fundamentals',
        description: 'Understand payers, plans, patient responsibility, benefits, deductibles, and coordination of benefits.',
        readTime: 10,
        completed: false
      }
    ]
  },
  {
    id: 'diagnostic-coding',
    number: '02',
    title: 'Diagnostic Coding (ICD-10-CM)',
    shortTitle: 'ICD-10-CM',
    description: 'Learn how diagnosis codes are structured, sequenced, verified, and supported by provider documentation.',
    icon: 'Activity',
    accent: 'blue',
    lessons: [
      {
        id: 'icd-10-structure',
        title: 'ICD-10-CM Code Structure',
        description: 'Explore categories, subcategories, placeholders, seventh characters, and levels of specificity.',
        readTime: 11,
        completed: false
      },
      {
        id: 'coding-conventions',
        title: 'Conventions and Guidelines',
        description: 'Apply Includes, Excludes, Code First, Use Additional Code, and other essential instructions.',
        readTime: 14,
        completed: false
      },
      {
        id: 'diagnosis-sequencing',
        title: 'Diagnosis Selection and Sequencing',
        description: 'Practice selecting the first-listed diagnosis and arranging secondary conditions correctly.',
        readTime: 12,
        completed: false
      }
    ]
  },
  {
    id: 'procedural-coding',
    number: '03',
    title: 'Procedural Coding (CPT® & HCPCS Level II)',
    shortTitle: 'CPT & HCPCS',
    description: 'Study procedure code families, modifiers, evaluation and management concepts, and supply coding.',
    icon: 'Clipboard',
    accent: 'violet',
    lessons: [
      {
        id: 'cpt-codebook',
        title: 'Navigating the CPT® Code Set',
        description: 'Understand CPT sections, symbols, guidelines, parenthetical notes, and code selection workflow.',
        readTime: 12,
        completed: false
      },
      {
        id: 'modifiers-basics',
        title: 'Modifier Fundamentals',
        description: 'Learn how common modifiers communicate distinct services and special procedural circumstances.',
        readTime: 10,
        completed: false
      },
      {
        id: 'hcpcs-level-two',
        title: 'HCPCS Level II Essentials',
        description: 'Review codes for supplies, equipment, medications, ambulance services, and selected modifiers.',
        readTime: 9,
        completed: false
      }
    ]
  },
  {
    id: 'revenue-cycle',
    number: '04',
    title: 'Healthcare Revenue Cycle & Billing Operations',
    shortTitle: 'Revenue Cycle',
    description: 'Follow a claim from patient registration through charge capture, adjudication, payment, and follow-up.',
    icon: 'TrendingUp',
    accent: 'amber',
    lessons: [
      {
        id: 'revenue-cycle-overview',
        title: 'The Revenue Cycle Journey',
        description: 'Map the stages that connect patient access, clinical care, coding, billing, and collections.',
        readTime: 9,
        completed: false
      },
      {
        id: 'clean-claims',
        title: 'Clean Claims and Adjudication',
        description: 'Identify claim components, common edits, payer decisions, and remittance advice terminology.',
        readTime: 13,
        completed: false
      },
      {
        id: 'denial-management',
        title: 'Denial Prevention and Follow-Up',
        description: 'Recognize frequent denial causes and use a structured process for correction and appeal.',
        readTime: 11,
        completed: false
      }
    ]
  },
  {
    id: 'compliance-regulatory',
    number: '05',
    title: 'Compliance & Regulatory Environment',
    shortTitle: 'Compliance',
    description: 'Understand privacy, fraud and abuse safeguards, ethical coding, audits, and compliance programs.',
    icon: 'Shield',
    accent: 'rose',
    lessons: [
      {
        id: 'hipaa-privacy',
        title: 'HIPAA Privacy and Security',
        description: 'Review protected health information, minimum necessary use, safeguards, and patient rights.',
        readTime: 10,
        completed: false
      },
      {
        id: 'fraud-waste-abuse',
        title: 'Fraud, Waste, and Abuse',
        description: 'Distinguish improper practices and learn why accurate, supported coding protects patients and organizations.',
        readTime: 12,
        completed: false
      },
      {
        id: 'coding-compliance-programs',
        title: 'Coding Compliance Programs',
        description: 'Explore audits, education, policies, reporting channels, and standards of ethical conduct.',
        readTime: 9,
        completed: false
      }
    ]
  }
];

export const domains = baseDomains.map((domain) => ({
  ...domain,
  lessons: domain.lessons.map((lesson) => ({
    ...lesson,
    ...lessonContent[lesson.id]
  }))
}));

export const allLessons = domains.flatMap((domain) =>
  domain.lessons.map((lesson) => ({
    ...lesson,
    domainId: domain.id,
    domainTitle: domain.title
  }))
);

export const totalLessonCount = allLessons.length;